import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { useCart } from '../context/CartContext';
import Modal from '../components/Modal';
import { getOptimizedImageUrl, preloadImages } from '../utils/imageOptimization';
import './ProductPage.css';

function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedLength, setSelectedLength] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [mainImage, setMainImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [modal, setModal] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });
  const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      setIsLoading(true);
      try {
        const productDoc = await getDoc(doc(db, 'products', productId));
        if (productDoc.exists()) {
          setProduct({ id: productDoc.id, ...productDoc.data() });
        } else {
          setProduct(null);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        setProduct(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  useEffect(() => {
    if (!product || !product.id) return;
    try {
      const viewKey = `viewed_product_${product.id}`;
      if (sessionStorage.getItem(viewKey)) return;
      sessionStorage.setItem(viewKey, '1');
    } catch (error) {
      console.warn('Session storage unavailable, logging view anyway.', error);
    }

    const recordView = async () => {
      try {
        await addDoc(collection(db, 'productViews'), {
          productId: product.id,
          productName: product.name || '',
          viewedAt: serverTimestamp(),
        });
      } catch (error) {
        console.warn('Failed to record product view:', error);
      }
    };

    recordView();
  }, [product]);

  useEffect(() => {
    if (product) {
      // Get all product images
      const allImages = (() => {
        if (product.images && Array.isArray(product.images)) {
          const uniqueImages = [];
          const seen = new Set();
          product.images.forEach(img => {
            const trimmed = img ? img.trim() : '';
            if (trimmed && !seen.has(trimmed)) {
              seen.add(trimmed);
              uniqueImages.push(trimmed);
            }
          });
          return uniqueImages.length > 0 ? uniqueImages : (product.image ? [product.image] : []);
        }
        return product.image ? [product.image] : [];
      })();

      // Set main image from product.image or first image in images array
      if (allImages.length > 0) {
        const optimizedFirstImage = getOptimizedImageUrl(allImages[0], 'product');
        setMainImage(optimizedFirstImage);
        setCurrentImageIndex(0);
        
        // Preload all product images for faster switching
        if (allImages.length > 1) {
          const optimizedImages = allImages.map(img => getOptimizedImageUrl(img, 'product'));
          preloadImages(optimizedImages).catch(err => {
            console.warn('Failed to preload some product images:', err);
          });
        }
      } else {
        // Fallback to placeholder
        setMainImage('https://via.placeholder.com/600x800?text=No+Image');
        setCurrentImageIndex(0);
      }
    }
  }, [product]);

  if (isLoading) {
    return (
      <div className="product-page">
        <main className="product-main">
          <div className="container">
            <div className="product-details product-details--loading">
              <div className="product-images">
                <div className="main-product-image skeleton"></div>
                <div className="product-thumbnails">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={`thumb-skeleton-${index}`} className="thumbnail skeleton"></div>
                  ))}
                </div>
              </div>

              <div className="product-info-section">
                <div className="skeleton skeleton-line skeleton-line--short"></div>
                <div className="skeleton skeleton-line skeleton-line--wide"></div>
                <div className="skeleton skeleton-line"></div>
                <div className="product-price-section">
                  <span className="skeleton skeleton-line skeleton-line--short"></span>
                  <span className="skeleton skeleton-line skeleton-line--short"></span>
                </div>
                <div className="product-options-primary">
                  <div className="option-group">
                    <div className="skeleton skeleton-line skeleton-line--short"></div>
                    <div className="size-options">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <div key={`size-skeleton-${index}`} className="size-option skeleton"></div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="product-actions product-actions--loading">
                  <div className="skeleton skeleton-line skeleton-line--wide"></div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-page">
        <main className="product-main">
          <div className="container">
            <div className="product-not-found">
              <h2>Product Not Found</h2>
              <button className="btn btn-primary" onClick={() => navigate('/')}>
                Back to Home
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Use product data from Firebase or fallback to defaults
  const productDetails = {
    description: product.description || 'Elegant abaya crafted with premium fabrics, featuring modern designs that blend traditional elegance with contemporary style. Perfect for special occasions or everyday wear. Made with the finest materials and attention to detail.',
    longDescription: product.longDescription || 'This exquisite abaya is designed to embrace the beauty of modesty while celebrating contemporary style and sophistication. Each piece is carefully crafted with high-quality fabrics, exquisite details, and meticulous craftsmanship. The elegant design makes it perfect for various occasions, from daily wear to special events.',
    sizes: product.sizes || ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
    colors: product.colors || [
      { name: 'Black', code: '#000000' },
      { name: 'Navy', code: '#1a2d5a' },
      { name: 'Burgundy', code: '#5a2a2a' },
      { name: 'Brown', code: '#8b7355' }
    ],
    material: product.material || '100% Premium Fabric',
    care: product.care || 'Dry Clean Only',
    availability: product.availability || 'In Stock',
    sku: product.sku || Math.floor(100000 + Math.random() * 900000).toString(),
    images: (() => {
      // If product has images array, filter out empty strings and duplicates
      if (product.images && Array.isArray(product.images)) {
        const uniqueImages = [];
        const seen = new Set();
        product.images.forEach(img => {
          const trimmed = img ? img.trim() : '';
          if (trimmed && !seen.has(trimmed)) {
            seen.add(trimmed);
            uniqueImages.push(trimmed);
          }
        });
        return uniqueImages.length > 0 ? uniqueImages : (product.image ? [product.image] : []);
      }
      // Fallback to single image
      return product.image ? [product.image] : [];
    })()
  };
  const availabilityRaw = productDetails.availability || 'In Stock';
  const availabilityKey = availabilityRaw.toLowerCase();
  const availabilityMarkedLow = availabilityKey.includes('few') || availabilityKey.includes('low') || availabilityKey.includes('left');
  const trackQuantity = product.trackQuantity !== false;
  const lowStockThreshold = Number(product.lowStockThreshold ?? 10);
  const stockValueRaw = product.stock ?? product.quantity;
  const stockValue = Number.isFinite(Number(stockValueRaw)) ? Number(stockValueRaw) : null;
  const availabilityIsOut = availabilityKey.includes('out') || availabilityKey.includes('sold') || (trackQuantity && stockValue !== null && stockValue <= 0);
  const isLowStock = !availabilityIsOut && (availabilityMarkedLow || (trackQuantity && stockValue !== null && stockValue > 0 && stockValue <= lowStockThreshold));
  const availabilityClass = 'is-out';
  const availabilityLabel = 'Sold Out';
  const isSoldOut = availabilityIsOut;

  const handleQuantityChange = (delta) => {
    setQuantity(prev => Math.max(1, Math.min(10, prev + delta)));
  };

  const handleNextImage = () => {
    if (productDetails.images.length > 1) {
      const nextIndex = (currentImageIndex + 1) % productDetails.images.length;
      const optimizedImage = getOptimizedImageUrl(productDetails.images[nextIndex], 'product');
      setCurrentImageIndex(nextIndex);
      setMainImage(optimizedImage);
    }
  };

  const lengthOptions = Array.from({ length: 15 }, (_, index) => {
    const value = 50 + index * 0.5;
    return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1);
  });

  const handleAddToCart = () => {
    if (isSoldOut) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Sold Out',
        message: 'This item is currently sold out.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }
    if (!selectedSize) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Size Required',
        message: 'Please select a size before adding to cart.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }
    if (!selectedLength) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Length Required',
        message: 'Please select a length before adding to cart.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }
    addToCart(product, selectedSize, null, selectedLength, quantity, notes);
    
    // Reset notes after adding to cart
    setNotes('');
    
    // Show success message and redirect to cart
    setModal({
      isOpen: true,
      type: 'success',
      title: 'Added to Cart',
      message: `Added ${quantity} x ${product.name} to cart!\n\nGo to cart now?`,
      onConfirm: () => {
        navigate('/cart');
        setModal({ ...modal, isOpen: false });
      },
      showCancel: true,
      confirmText: 'Go to Cart',
      cancelText: 'Continue Shopping'
    });
  };

  return (
    <div className="product-page">
      <main className="product-main">
        <div className="container">
          <div className="product-details">
            <div className="product-images">
              <div 
                className="main-product-image"
                style={{
                  backgroundImage: mainImage ? `url(${mainImage})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundColor: mainImage ? 'transparent' : '#f0f0f0',
                  cursor: productDetails.images.length > 1 ? 'pointer' : 'default'
                }}
                onClick={productDetails.images.length > 1 ? handleNextImage : undefined}
                onMouseEnter={(e) => {
                  if (productDetails.images.length > 1) {
                    e.currentTarget.style.opacity = '0.95';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                title={productDetails.images.length > 1 ? 'Click to view next image' : ''}
              >
                {product.badge && (
                  <div className={`product-page-badge ${product.badge.toLowerCase().replace(' ', '-')}`}>
                    {product.badge}
                  </div>
                )}
              </div>
              
              <div className="product-thumbnails">
                {productDetails.images.length > 0 ? (
                  productDetails.images.map((image, index) => (
                    <div
                      key={index}
                      className={`thumbnail ${mainImage === image ? 'active' : ''}`}
                      style={{
                        backgroundImage: image ? `url(${image})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundColor: image ? 'transparent' : '#f0f0f0'
                      }}
                      onClick={() => {
                        if (image) {
                          const optimizedImage = getOptimizedImageUrl(image, 'product');
                          setMainImage(optimizedImage);
                          setCurrentImageIndex(index);
                        }
                      }}
                    ></div>
                  ))
                ) : (
                  <div className="thumbnail" style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}>
                    <span style={{ fontSize: '0.75rem', color: '#999' }}>No images</span>
                  </div>
                )}
              </div>
            </div>

            <div className="product-info-section">
              <button 
                className="back-button-icon" 
                onClick={() => navigate('/shop')}
                aria-label="Go back to shop"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
              <p className="product-brand">THE TAG</p>
              {product.badge && (
                <div className={`product-badge-header ${product.badge.toLowerCase().replace(' ', '-')}`}>
                  {product.badge}
                </div>
              )}
              
              {availabilityIsOut && (
                <div className="sold-out-kicker">Sold Out</div>
              )}
              {!availabilityIsOut && isLowStock && (
                <div className="low-stock-kicker">Few Pieces Left</div>
              )}
              <h1 className="product-title">{product.name}</h1>

              <div className="product-price-section">
                <span className="current-price">
                  {product.price.toFixed(2)} ر.ق
                </span>
                {product.originalPrice && (
                  <span className="original-price">
                    {product.originalPrice.toFixed(2)} ر.ق
                  </span>
                )}
              </div>

              

              {/* Primary Options - Size Selection */}
              <div className="product-options-primary">
                <div className="option-group">
                  <label className="option-label">Size</label>
                  <div className="size-options">
                    {productDetails.sizes.map((size) => (
                      <button
                        key={size}
                        className={`size-option ${selectedSize === size ? 'active' : ''}`}
                        onClick={() => setSelectedSize(size)}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="option-group">
                  <label className="option-label" htmlFor="length-select">Length</label>
                  <select
                    id="length-select"
                    className="length-select"
                    value={selectedLength ?? ''}
                    onChange={(e) => setSelectedLength(e.target.value || null)}
                  >
                    <option value="">Select length</option>
                    {lengthOptions.map((length) => (
                      <option key={length} value={length}>
                        {length}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Add to Cart - Primary Action */}
              <div className="product-actions product-actions-primary">
                <button
                  className="btn btn-primary add-cart-btn-large"
                  onClick={handleAddToCart}
                  disabled={isSoldOut}
                  aria-disabled={isSoldOut}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <path d="M16 10a4 4 0 0 1-8 0"></path>
                  </svg>
                  {isSoldOut ? 'Sold Out' : 'Add to Cart'}
                </button>
              </div>

              {/* Secondary Options - Collapsible/Secondary on Mobile */}
              <div className="product-options-secondary">
                <div className={`product-size-chart-inline ${isSizeChartOpen ? 'size-chart-open' : ''}`}>
                  <button 
                    className="size-chart-toggle"
                    onClick={() => setIsSizeChartOpen(!isSizeChartOpen)}
                    aria-expanded={isSizeChartOpen}
                  >
                    <h3>Size Chart</h3>
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      style={{ transform: isSizeChartOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  <div className="size-chart-content">
                    <p className="size-chart-note">All measurements are in inches</p>
                    <div className="size-chart-table-wrapper">
                      <table className="size-chart-table">
                        <thead>
                          <tr>
                            <th>Size</th>
                            <th>BUST</th>
                            <th>SHOULDER</th>
                            <th>SLEEVE</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>S</td>
                            <td>19-20</td>
                            <td>14</td>
                            <td>22</td>
                          </tr>
                          <tr>
                            <td>M</td>
                            <td>22-24</td>
                            <td>15</td>
                            <td>23</td>
                          </tr>
                          <tr>
                            <td>L</td>
                            <td>24-26</td>
                            <td>16</td>
                            <td>24</td>
                          </tr>
                          <tr>
                            <td>XL</td>
                            <td>27-29</td>
                            <td>17</td>
                            <td>25</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="option-group">
                  <label className="option-label">Quantity</label>
                  <div className="quantity-selector">
                    <button 
                      className="quantity-btn"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                    >
                      −
                    </button>
                    <span className="quantity-value">{quantity}</span>
                    <button 
                      className="quantity-btn"
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= 10}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="option-group">
                  <label className="option-label">Notes (Optional)</label>
                  <textarea
                    className="product-notes-input"
                    placeholder="Add any special instructions or customization requests..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    maxLength={500}
                  />
                  <span className="notes-character-count">{notes.length}/500</span>
                </div>
              </div>

              {/* Sticky Add to Cart for Mobile */}
              <div className="product-actions-sticky">
                <button
                  className="btn btn-primary add-cart-btn-large"
                  onClick={handleAddToCart}
                  disabled={isSoldOut}
                  aria-disabled={isSoldOut}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <path d="M16 10a4 4 0 0 1-8 0"></path>
                  </svg>
                  {isSoldOut ? 'Sold Out' : 'Add to Cart'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onConfirm={modal.onConfirm}
        confirmText={modal.confirmText || 'OK'}
        cancelText={modal.cancelText || 'Cancel'}
        showCancel={modal.showCancel !== undefined ? modal.showCancel : false}
      />
    </div>
  );
}

export default ProductPage;
